import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ShoppingCart, Truck, Calculator, MapPin, CheckSquare, Save, ShieldCheck, CreditCard, Webhook } from 'lucide-react';

const BSSI_APP_ID = '52';
const BSSI_WEBHOOK_URL = 'https://spedireonline.net/api/hooks/bssi';

const CLEAR_FIELDS = ['woo_store_url', 'spedireonline_base_url', 'spedireonline_user', 'pricing_base_url', 'pricing_listino_id', 'checkaddress_base_url', 'bssi_webhook_key_id', 'bssi_webhook_key_id_prev'];
const SECRET_FIELDS = ['woo_consumer_key', 'woo_consumer_secret', 'spedireonline_token', 'spedireonline_pass', 'pricing_token', 'google_maps_api_key', 'checkaddress_key', 'stripe_secret_key', 'bssi_webhook_secret', 'bssi_webhook_secret_prev'];

const GROUPS = [
  {
    key: 'woo', title: 'WooCommerce (Database utenti & ordini)', icon: ShoppingCart,
    fields: [
      { name: 'woo_store_url', label: 'URL Negozio', secret: false, placeholder: 'https://www.negozio.it/shop' },
      { name: 'woo_consumer_key', label: 'Consumer Key', secret: true, placeholder: 'ck_...' },
      { name: 'woo_consumer_secret', label: 'Consumer Secret', secret: true, placeholder: 'cs_...' },
    ],
  },
  {
    key: 'sped', title: 'SpedireOnline (Motore spedizioni)', icon: Truck,
    fields: [
      { name: 'spedireonline_base_url', label: 'Base URL', secret: false, placeholder: 'https://web.spedireonline.cloud/globaltrs' },
      { name: 'spedireonline_user', label: 'Utente (Basic Auth)', secret: false, placeholder: 'KIP210175' },
      { name: 'spedireonline_pass', label: 'Password (Basic Auth)', secret: true, placeholder: '••••' },
      { name: 'spedireonline_token', label: 'Token (opzionale)', secret: true, placeholder: '••••' },
    ],
  },
  {
    key: 'price', title: 'Motore Prezzi (Listino)', icon: Calculator,
    fields: [
      { name: 'pricing_base_url', label: 'Base URL', secret: false, placeholder: 'https://web.spedireonline.cloud/logiaudit-api' },
      { name: 'pricing_listino_id', label: 'Codice Listino (listino_id)', secret: false, placeholder: 'efeaba6d-...' },
      { name: 'pricing_token', label: 'Token (Bearer)', secret: true, placeholder: '••••' },
    ],
  },
  {
    key: 'google', title: 'Google Maps (Autocomplete indirizzi)', icon: MapPin,
    fields: [
      { name: 'google_maps_api_key', label: 'Google API Key', secret: true, placeholder: 'AIza...' },
    ],
  },
  {
    key: 'check', title: 'CheckAddress / LogiAddress (Validazione indirizzi)', icon: CheckSquare,
    fields: [
      { name: 'checkaddress_base_url', label: 'Base URL', secret: false, placeholder: 'https://.../api/v1' },
      { name: 'checkaddress_key', label: 'API Key (x-api-key)', secret: true, placeholder: 'avk_...' },
    ],
  },
  {
    key: 'stripe', title: 'Stripe (Pagamenti)', icon: CreditCard,
    fields: [
      { name: 'stripe_secret_key', label: 'Secret / Restricted Key', secret: true, placeholder: 'sk_test_... o rk_test_...' },
    ],
  },
  {
    key: 'bssi', title: 'BSSI Marketplace (Webhook spedizioni)', icon: Webhook,
    fields: [
      { name: 'bssi_webhook_secret', label: 'Segreto webhook (corrente)', secret: true, placeholder: 'ricevuto da BSSI su canale sicuro' },
      { name: 'bssi_webhook_key_id', label: 'Key ID corrente (X-BSSI-Key-Id)', secret: false, placeholder: 'es. k1 (opzionale)' },
      { name: 'bssi_webhook_secret_prev', label: 'Segreto precedente (rotazione)', secret: true, placeholder: 'solo durante una rotazione' },
      { name: 'bssi_webhook_key_id_prev', label: 'Key ID precedente', secret: false, placeholder: 'es. k0 (opzionale)' },
    ],
  },
];

export const IntegrationsConfig = ({ token, API }) => {
  const [config, setConfig] = useState({});
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/integration-config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      const initial = {};
      CLEAR_FIELDS.forEach((f) => { initial[f] = res.data[f] || ''; });
      SECRET_FIELDS.forEach((f) => { initial[f] = ''; });
      initial.bssi_integration_enabled = !!res.data.bssi_integration_enabled;
      setForm(initial);
    } catch (error) {
      toast.error('Errore nel caricamento della configurazione');
    } finally {
      setLoading(false);
    }
  }, [API, token]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleChange = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {};
      CLEAR_FIELDS.forEach((f) => { payload[f] = form[f] ?? ''; });
      SECRET_FIELDS.forEach((f) => { if (form[f] && form[f].trim() !== '') payload[f] = form[f].trim(); });
      payload.bssi_integration_enabled = !!form.bssi_integration_enabled;
      await axios.put(`${API}/admin/integration-config`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Configurazione integrazioni salvata!');
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="integrations-config">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" />
          Configurazione Integrazioni
        </CardTitle>
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          Chiavi e credenziali dei sistemi esterni (WooCommerce, SpedireOnline, Motore Prezzi, Google, CheckAddress).
          I segreti sono conservati solo lato server e mostrati mascherati. Lascia vuoto un campo segreto per mantenere il valore attuale.
        </p>
      </CardHeader>
      <CardContent className="space-y-8">
        {loading ? (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Caricamento...</p>
        ) : (
          <>
            {GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <div key={group.key} data-testid={`integration-group-${group.key}`}>
                  <h3 className="text-base font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                    <Icon className="w-4 h-4" />
                    {group.title}
                  </h3>
                  {group.key === 'bssi' && (
                    <div className="mb-4 space-y-3">
                      <div className="text-xs rounded-md p-3 space-y-1" style={{ background: '#F1F5F9', color: 'var(--text-secondary)' }}>
                        <div>App ID BSSI: <strong>{BSSI_APP_ID}</strong> (fisso, non va inviato nelle richieste)</div>
                        <div>URL ricevente (da impostare in <em>Connetti Marketplace</em> lato BSSI): <code>{BSSI_WEBHOOK_URL}</code></div>
                        <div>Ambiente di test distinto dall'header <code>X-BSSI-Env: test</code> · firma HMAC-SHA256 · segreto ruotabile (usa i campi "precedente" solo durante una rotazione).</div>
                      </div>
                      <label className="flex items-center gap-2 text-sm font-medium cursor-pointer" style={{ color: 'var(--text-primary)' }}>
                        <input
                          type="checkbox"
                          checked={!!form.bssi_integration_enabled}
                          onChange={(e) => handleChange('bssi_integration_enabled', e.target.checked)}
                          data-testid="integration-input-bssi_integration_enabled"
                        />
                        Integrazione BSSI attiva
                      </label>
                    </div>
                  )}
                  <div className="grid gap-4 md:grid-cols-2">
                    {group.fields.map((field) => {
                      const isSet = config[`${field.name}_set`];
                      const masked = config[field.name];
                      return (
                        <div key={field.name}>
                          <label className="text-sm font-medium flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                            {field.label}
                            {field.secret && isSet && (
                              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: '#DCFCE7', color: '#166534' }}>
                                ✓ configurato {masked}
                              </span>
                            )}
                          </label>
                          <Input
                            type={field.secret ? 'password' : 'text'}
                            value={form[field.name] ?? ''}
                            onChange={(e) => handleChange(field.name, e.target.value)}
                            placeholder={field.secret && isSet ? '•••••••• (invariato)' : field.placeholder}
                            className="mt-1"
                            data-testid={`integration-input-${field.name}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="pt-2">
              <Button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-integrations-btn">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Salvataggio...' : 'Salva Configurazione'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrationsConfig;
